import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../lib/queryClient.ts";
import { showSuccess, showError } from "../lib/toastHelpers.ts";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface UseApiMutationOptions<TData = any, TVariables = any> {
  endpoint: string | ((variables: TVariables) => string);
  method: HttpMethod | ((variables: TVariables) => HttpMethod);
  invalidate?: any[];
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  errorMessage?: string | ((error: any, variables: TVariables) => string);
  onMutate?: (variables: TVariables) => void;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: any, variables: TVariables) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  customFetch?: (url: string, options: any) => Promise<Response>;
}

export function useApiMutation<TData = any, TVariables = any>({
  endpoint,
  method,
  invalidate = [],
  successMessage,
  errorMessage,
  onMutate,
  onSuccess,
  onError,
  showSuccessToast = true,
  showErrorToast = true,
  customFetch,
}: UseApiMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();
  return useMutation<TData, any, TVariables>({
    onMutate: onMutate ? (variables) => { onMutate(variables); } : undefined,
    mutationFn: async (variables: TVariables) => {
      const url = typeof endpoint === "function" ? endpoint(variables) : endpoint;
      const resolvedMethod = typeof method === "function" ? method(variables) : method;
      
      // Use customFetch if provided
      if (customFetch) {
        const body = (resolvedMethod !== "DELETE" && resolvedMethod !== "GET") ? variables : undefined;
        return await customFetch(url, { body: JSON.stringify(body), method: resolvedMethod }) as unknown as TData;
      }

      // Otherwise use default apiRequest
      const body = (resolvedMethod !== "DELETE" && resolvedMethod !== "GET") ? variables : undefined;
      return await apiRequest(resolvedMethod, url, body) as TData;
    },
    onSuccess: (data, variables) => {
      // Invalidate queries
      if (invalidate.length > 0) {
        invalidate.forEach((queryKey) => {
          // If queryKey is a function, call it with variables to get the actual key
          const actualKey = typeof queryKey === 'function' ? queryKey(variables) : queryKey;
          queryClient.invalidateQueries({ queryKey: actualKey });
        });
      }

      // Show success toast
      if (showSuccessToast && successMessage) {
        const msg = typeof successMessage === "function" ? successMessage(data, variables) : successMessage;
        showSuccess(msg);
      }

      // Call custom onSuccess
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      // Show error toast
      if (showErrorToast) {
        const message = (typeof errorMessage === "function" ? errorMessage(error, variables) : errorMessage) || error?.message || "An error occurred";
        showError(message);
      }

      // Call custom onError
      onError?.(error, variables);
    },
  });
}
